import { useState, useEffect, useRef } from 'react'
import { getActorMovies, getMovieCast, getMovieCastFull, posterUrl, profileUrl } from '../../api/tmdb'
import { updateParty } from '../../utils/partyGame'
import Filmstrip from '../Filmstrip'
import Confetti from '../Confetti'
import { IconArrowRight, IconClock, IconClapper, IconTarget, IconStar, IconMasks } from '../icons'

const INACTIVITY_MS = 2 * 60 * 1000 // 2 minutes before Cameo prompt

const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='342' height='513' viewBox='0 0 342 513'%3E%3Crect fill='%23333' width='342' height='513'/%3E%3C/svg%3E"
const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

function fmtTime(ms) {
  if (!ms) return '00:00'
  const s = Math.floor(ms / 1000)
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Co-Op gameplay — one component, three roles:
 *   role='host'    → big-screen view: chain + turn queue + whose turn
 *   role='active'  → active player phone: pick movie → pick actor → pass torch
 *   role='watch'   → non-active player phone: chain + whose turn
 */
export default function PartyCoopGameplay({ party, currentUserId, role }) {
  const players       = party?.players ?? {}
  const turnOrder     = party?.turnOrder ?? []
  const turnIndex     = party?.currentTurnIndex ?? 0
  const coopChain     = party?.coopChain ?? []
  const playerMoves   = party?.playerMoves ?? {}
  const goalActor     = party?.goalActor
  const startActor    = party?.startActor

  const activeUid     = turnOrder[turnIndex] ?? null
  const activePlayer  = activeUid ? players[activeUid] : null
  const isMyTurn      = activeUid && currentUserId === activeUid

  // Current actor = last actor in coopChain, or fall back to startActor
  const lastActor = [...coopChain].reverse().find((c) => c.type === 'actor') || startActor

  // ── Tick clock for elapsed time + inactivity timer ────────────────────────
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // ── Celebration: when status flips to 'coop-results', fire confetti on
  // every device that's still rendering this component (typically the active
  // player who just picked the goal — for the brief window before routing to
  // PartyCoopDone). PartyCoopDone also renders its own confetti so the burst
  // continues seamlessly across the transition.
  // TODO: apply this same pattern to Director's Cut when that mode is built.
  const [celebrating, setCelebrating] = useState(false)
  useEffect(() => {
    if (party?.status !== 'coop-results') return
    setCelebrating(true)
    const id = setTimeout(() => setCelebrating(false), 4000)
    return () => clearTimeout(id)
  }, [party?.status])

  const playingStartMs = party?.playingStartedAt?.toMillis?.()
                       ?? (party?.playingStartedAt?.seconds ? party.playingStartedAt.seconds * 1000 : null)
  const elapsedMs   = playingStartMs ? Date.now() - playingStartMs : 0

  const turnStartMs = party?.turnStartedAt?.toMillis?.()
                    ?? (party?.turnStartedAt?.seconds ? party.turnStartedAt.seconds * 1000 : null)
  const turnElapsedMs = turnStartMs ? Date.now() - turnStartMs : 0
  const showCameoPrompt = isMyTurn && role === 'active' && turnElapsedMs >= INACTIVITY_MS

  // ── HOST view ─────────────────────────────────────────────────────────────
  if (role === 'host') {
    const activeInitial = (activePlayer?.username || 'G').charAt(0).toUpperCase()
    return (
      <div className="coop-host">
        {celebrating && <Confetti />}
        <div className="coop-host__header">
          <div className="coop-host__matchup">
            <CoopActorThumb actor={startActor} label="Start" />
            <span className="coop-host__arrow" aria-hidden="true">
              <IconArrowRight size={20} />
            </span>
            <CoopActorThumb actor={goalActor} label="Goal" />
          </div>
          <div className="coop-host__elapsed">
            <IconClock size={20} aria-hidden="true" />
            <span>{fmtTime(elapsedMs)}</span>
          </div>
        </div>

        {/* Sprocket-hole framed filmstrip — matches the Win Screen chain
            treatment. Adds perforated rows top + bottom via CSS pseudo-elements. */}
        <div className="coop-host__chain-wrap coop-host__chain-wrap--filmstrip">
          {coopChain.length > 0 ? (
            <Filmstrip chain={coopChain} onItemClick={() => {}} hidePlaceholder followLiveEdge />
          ) : (
            <p className="coop-host__chain-empty">The chain begins…</p>
          )}
        </div>

        <div className="coop-host__turn">
          <p className="coop-host__turn-label">NOW FILMING</p>
          <div className="coop-host__turn-card">
            <div className="coop-host__turn-avatar coop-host__turn-avatar--glow">
              {activePlayer?.avatar
                ? <img src={activePlayer.avatar} alt="" />
                : <span className="party-avatar__initial">{activeInitial}</span>}
            </div>
            <p className="coop-host__turn-name">{activePlayer?.username || 'Guest'}</p>
            <p className="coop-host__turn-actor">
              Picking from <strong>{lastActor?.name || '…'}</strong>
            </p>
          </div>
        </div>

        {turnOrder.length > 1 && (
          <div className="coop-host__queue">
            <p className="coop-host__queue-label">TURN ORDER</p>
            <div className="coop-host__queue-strip">
              {turnOrder.map((uid, i) => {
                const p = players[uid]
                const isActive = i === turnIndex
                const initial = (p?.username || 'G').charAt(0).toUpperCase()
                return (
                  <div
                    key={uid}
                    className={`coop-host__queue-chip${isActive ? ' coop-host__queue-chip--active' : ''}`}
                  >
                    <div className="coop-host__queue-avatar">
                      {p?.avatar
                        ? <img src={p.avatar} alt="" />
                        : <span className="party-avatar__initial">{initial}</span>}
                    </div>
                    <span className="coop-host__queue-name">{p?.username || 'Guest'}</span>
                    <span className="coop-host__queue-moves">
                      {playerMoves[uid] || 0} move{(playerMoves[uid] || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── WATCHING player view ──────────────────────────────────────────────────
  if (role === 'watch' || !isMyTurn) {
    return (
      <div className="coop-watch">
        {celebrating && <Confetti />}
        <div className="coop-watch__header">
          <div className="coop-watch__avatar">
            {activePlayer?.avatar
              ? <img src={activePlayer.avatar} alt="" />
              : <IconClapper size={20} />}
          </div>
          <p className="coop-watch__title">
            <strong>{activePlayer?.username || 'Guest'}</strong> is up
          </p>
          <p className="coop-watch__sub">
            picking from <strong>{lastActor?.name || '…'}</strong>
          </p>
        </div>

        {coopChain.length > 0 && (
          <div className="coop-watch__chain">
            <Filmstrip chain={coopChain} onItemClick={() => {}} hidePlaceholder followLiveEdge />
          </div>
        )}

        <div className="coop-watch__goal">
          <p className="coop-watch__goal-label"><IconTarget size={15} style={{ verticalAlign: 'middle' }} /> Goal</p>
          <CoopActorThumb actor={goalActor} />
        </div>

        <p className="coop-watch__pulse">Stand by — your turn's coming…</p>
      </div>
    )
  }

  // ── ACTIVE player view ────────────────────────────────────────────────────
  return (
    <ActiveTurn
      party={party}
      currentUserId={currentUserId}
      players={players}
      turnOrder={turnOrder}
      turnIndex={turnIndex}
      coopChain={coopChain}
      playerMoves={playerMoves}
      lastActor={lastActor}
      goalActor={goalActor}
      showCameoPrompt={showCameoPrompt}
      celebrating={celebrating}
    />
  )
}

// ── Active turn — picks a movie, then an actor, then passes the torch ─────
function ActiveTurn({ party, currentUserId, players, turnOrder, turnIndex, coopChain, playerMoves, lastActor, goalActor, showCameoPrompt, celebrating }) {
  const [view, setView]               = useState('movies') // 'movies' | 'cast'
  const [movies, setMovies]           = useState([])
  const [cast, setCast]               = useState([])
  const [selectedMovie, setSelected]  = useState(null)
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState('')
  const [committing, setCommitting]   = useState(false)
  const [cameoLoading, setCameoLoading] = useState(false)

  // Track ids already used in chain so we can disable them
  const usedActorIds = new Set(coopChain.filter(c => c.type === 'actor').map(c => c.id))
  const usedMovieIds = new Set(coopChain.filter(c => c.type === 'movie').map(c => c.id))

  // Reset to movies view whenever lastActor changes (new turn starts)
  const lastActorIdRef = useRef(lastActor?.id)
  useEffect(() => {
    if (lastActor?.id !== lastActorIdRef.current) {
      lastActorIdRef.current = lastActor?.id
      setView('movies')
      setSelected(null)
      setCast([])
    }
  }, [lastActor?.id])

  // Load filmography for lastActor — full popularity-sorted list (matches single-player)
  useEffect(() => {
    if (!lastActor?.id || view !== 'movies') return
    setLoading(true); setError('')
    getActorMovies(lastActor.id)
      .then((m) => setMovies(m))
      .catch(() => setError('Could not load movies'))
      .finally(() => setLoading(false))
  }, [lastActor?.id, view])

  // Inject the goal actor into a movie's cast if it's missing but actually present
  // in the full cast — mirrors single-player's injectGuaranteedActors logic.
  const injectGoalActor = async (cast, movieId) => {
    if (!goalActor) return cast
    if (cast.some((c) => c.id === goalActor.id)) return cast
    try {
      const full = await getMovieCastFull(movieId)
      const entry = full.find((c) => c.id === goalActor.id)
      if (!entry) return cast // goal actor genuinely isn't in this movie
      // Replace the last slot so the cast size stays stable
      const keep = Math.max(0, cast.length - 1)
      return [...cast.slice(0, keep), entry]
    } catch {
      return cast
    }
  }

  const handleMovieTap = async (movie) => {
    if (usedMovieIds.has(movie.id)) return
    setSelected(movie); setView('cast'); setLoading(true); setError('')
    try {
      const c = await getMovieCast(movie.id)
      const finalCast = await injectGoalActor(c, movie.id)
      setCast(finalCast)
    } catch {
      setError('Could not load cast')
    } finally {
      setLoading(false)
    }
  }

  const handleActorTap = async (actor) => {
    if (usedActorIds.has(actor.id) || committing) return
    if (actor.id === lastActor?.id) return  // can't pick the same actor
    setCommitting(true)
    const movieItem = {
      type: 'movie',
      id: selectedMovie.id,
      title: selectedMovie.title,
      poster_path: selectedMovie.poster_path ?? null,
    }
    const actorItem = {
      type: 'actor',
      id: actor.id,
      name: actor.name,
      profile_path: actor.profile_path ?? null,
    }
    const newChain = [...coopChain, movieItem, actorItem]
    const isGoal   = actor.id === goalActor?.id
    const newMoves = { ...playerMoves, [currentUserId]: (playerMoves[currentUserId] || 0) + 1 }

    const updates = {
      coopChain: newChain,
      playerMoves: newMoves,
    }
    if (isGoal) {
      updates.status      = 'coop-results'
      updates.finishedAt  = new Date()
      updates.solvedAt    = new Date()   // broadcast win moment to all devices for confetti sync
    } else {
      updates.currentTurnIndex = (turnIndex + 1) % turnOrder.length
      updates.turnStartedAt    = new Date()
    }
    try {
      await updateParty(party.id, updates)
    } catch {
      setCommitting(false)
    }
  }

  const handlePass = async () => {
    if (committing) return
    setCommitting(true)
    const prevPassed = Array.isArray(party.passedUids) ? party.passedUids : []
    const passedUids = prevPassed.includes(currentUserId) ? prevPassed : [...prevPassed, currentUserId]
    try {
      await updateParty(party.id, {
        currentTurnIndex: (turnIndex + 1) % turnOrder.length,
        turnStartedAt: new Date(),
        passedUids,
      })
    } catch {
      setCommitting(false)
    }
  }

  // ── Cameo: try to find a connection (current actor → goal, or via any movie)
  const handleCameo = async () => {
    if (cameoLoading) return
    setCameoLoading(true)
    try {
      // Strategy 1: Does currentActor share a movie with goalActor? Direct win!
      const acts = await getActorMovies(lastActor.id)
      const candidates = acts.filter(m => !usedMovieIds.has(m.id)).slice(0, 12)
      for (const m of candidates) {
        const c = await getMovieCast(m.id)
        const hit = c.find(a => a.id === goalActor.id)
        if (hit) {
          // Direct connection to goal
          const newChain = [
            ...coopChain,
            { type: 'movie', id: m.id, title: m.title, poster_path: m.poster_path ?? null },
            { type: 'actor', id: goalActor.id, name: goalActor.name, profile_path: goalActor.profile_path ?? null },
          ]
          // Cameos count as a move so refund logic stays fair
          const newMoves = { ...playerMoves, [currentUserId]: (playerMoves[currentUserId] || 0) + 1 }
          await updateParty(party.id, {
            coopChain: newChain,
            playerMoves: newMoves,
            cameoLastBy: players[currentUserId]?.username || 'Guest',
            status: 'coop-results',
            finishedAt: new Date(),
            solvedAt: new Date(),   // broadcast win moment to all devices for confetti sync
          })
          return
        }
      }
      // Strategy 2: pick a popular non-used movie + non-used non-current cast member
      for (const m of candidates) {
        const c = await getMovieCast(m.id)
        const next = c.find(a => !usedActorIds.has(a.id) && a.id !== lastActor.id && a.profile_path)
        if (next) {
          const newChain = [
            ...coopChain,
            { type: 'movie', id: m.id, title: m.title, poster_path: m.poster_path ?? null },
            { type: 'actor', id: next.id, name: next.name, profile_path: next.profile_path ?? null },
          ]
          const newMoves = { ...playerMoves, [currentUserId]: (playerMoves[currentUserId] || 0) + 1 }
          await updateParty(party.id, {
            coopChain: newChain,
            playerMoves: newMoves,
            cameoLastBy: players[currentUserId]?.username || 'Guest',
            currentTurnIndex: (turnIndex + 1) % turnOrder.length,
            turnStartedAt: new Date(),
          })
          return
        }
      }
      // No move found — just pass
      await handlePass()
    } catch {
      setCameoLoading(false)
    }
  }

  return (
    <div className="coop-active">
      {celebrating && <Confetti />}
      <div className="coop-active__chain-bar">
        {coopChain.length > 0 ? (
          <Filmstrip chain={coopChain} onItemClick={() => {}} hidePlaceholder followLiveEdge />
        ) : (
          <p className="coop-active__chain-empty">
            Start: <strong>{lastActor?.name}</strong> · Goal: <strong>{goalActor?.name}</strong>
          </p>
        )}
      </div>

      <div className="coop-active__header">
        <p className="coop-active__title"><IconClapper size={16} style={{ verticalAlign: 'middle' }} /> Your turn!</p>
        <p className="coop-active__sub">
          {view === 'movies'
            ? <>Pick a movie from <strong>{lastActor?.name}</strong></>
            : <>Pick an actor from <strong>{selectedMovie?.title}</strong></>}
        </p>
        <p className="coop-active__goal"><IconTarget size={15} style={{ verticalAlign: 'middle' }} /> Goal: <strong>{goalActor?.name}</strong></p>
      </div>

      {view === 'cast' && (
        <button
          type="button"
          className="btn-ghost coop-active__back"
          onClick={() => { setView('movies'); setSelected(null); setCast([]) }}
        >
          ← Pick a different movie
        </button>
      )}

      {loading && <div className="coop-active__loading"><div className="spinner" /></div>}
      {error && <p className="coop-active__error">{error}</p>}

      {!loading && view === 'movies' && (
        <div className="coop-active__grid coop-active__grid--movies">
          {movies.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`coop-active__card${usedMovieIds.has(m.id) ? ' coop-active__card--used' : ''}`}
              onClick={() => handleMovieTap(m)}
              disabled={usedMovieIds.has(m.id)}
            >
              <img src={m.poster_path ? posterUrl(m.poster_path) : MOVIE_PH} alt={m.title} />
              <span className="coop-active__card-name">{m.title}</span>
            </button>
          ))}
        </div>
      )}

      {!loading && view === 'cast' && (
        <div className="coop-active__grid coop-active__grid--actors">
          {cast.map((a) => {
            const isUsed = usedActorIds.has(a.id) || a.id === lastActor?.id
            const isGoal = a.id === goalActor?.id
            return (
              <button
                key={a.id}
                type="button"
                className={[
                  'coop-active__card',
                  isUsed ? 'coop-active__card--used' : '',
                  isGoal ? 'coop-active__card--goal' : '',
                ].join(' ').trim()}
                onClick={() => handleActorTap(a)}
                disabled={isUsed || committing}
              >
                <img src={a.profile_path ? profileUrl(a.profile_path) : ACTOR_PH} alt={a.name} />
                <span className="coop-active__card-name">
                  {a.name}{isGoal ? <> <IconTarget size={12} style={{ verticalAlign: 'middle' }} /></> : ''}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {showCameoPrompt && (
        <div className="coop-cameo-overlay">
          <div className="coop-cameo-dialog">
            <p className="coop-cameo-dialog__title">Lights are waiting... <IconClapper size={16} style={{ verticalAlign: 'middle' }} /></p>
            <p className="coop-cameo-dialog__body">The room is counting on you!</p>
            <div className="coop-cameo-dialog__actions">
              <button
                className="btn-primary"
                onClick={handleCameo}
                disabled={cameoLoading}
              >
                {cameoLoading ? 'Calling...' : <>Cameo <IconStar size={15} style={{ verticalAlign: 'middle' }} /></>}
              </button>
              <button
                className="btn-ghost"
                onClick={handlePass}
                disabled={committing}
              >
                Pass <IconMasks size={15} style={{ verticalAlign: 'middle' }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CoopActorThumb({ actor, label }) {
  if (!actor) return null
  return (
    <div className="coop-actor-thumb">
      {actor.profile_path && <img src={profileUrl(actor.profile_path, 'w185')} alt={actor.name} />}
      {label && <span className="coop-actor-thumb__label">{label}</span>}
      <span className="coop-actor-thumb__name">{actor.name}</span>
    </div>
  )
}
